import React, { useEffect, useRef, useState, forwardRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  KeyboardArrowLeft,
  KeyboardArrowRight,
} from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import { COLORS_ADMIN_PAGE, COLORS_CONFIG_PAGE } from '../../conf/constants';
import { TRANSACTION_HISTORY_TABLE_CONSTANTS } from '../constants/transactionHistoryTable';
import { formatCurrency, formatDateTime, getTransactionTypeInfo } from '../utils';
import { TransactionHistoryItem } from '../types';

interface TransactionHistoryTableProps {
  transactions: TransactionHistoryItem[];
  searchTerm: string;
  onSearchChange: (term: string) => void;
  currentPage: number;
  totalTransactions: number;
  transactionsPerPage: number;
  onPageChange: (page: number) => void;
  loading?: boolean;
  selectedTransactionTypes: string[];
  onTransactionTypeFilter: (types: string[]) => void;
  onTransactionsPerPageChange?: (transactionsPerPage: number) => void;
}

const TransactionHistoryTable = forwardRef<HTMLDivElement, TransactionHistoryTableProps>(({
  transactions,
  searchTerm,
  onSearchChange,
  currentPage,
  totalTransactions,
  transactionsPerPage,
  onPageChange,
  loading = false,
  selectedTransactionTypes,
  onTransactionTypeFilter,
  onTransactionsPerPageChange,
}, ref) => {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [calculatedTransactionsPerPage, setCalculatedTransactionsPerPage] = useState(transactionsPerPage);

  // Function to calculate number of rows that can be displayed
  const calculateTransactionsPerPage = () => {
    if (!tableContainerRef.current) return transactionsPerPage;

    const containerHeight = tableContainerRef.current.clientHeight;
    
    // Calculate available height for table rows
    const availableHeight = containerHeight 
      - TRANSACTION_HISTORY_TABLE_CONSTANTS.HEADER_HEIGHT 
      - TRANSACTION_HISTORY_TABLE_CONSTANTS.PADDING_BUFFER;
    
    const calculatedRows = Math.floor(availableHeight / TRANSACTION_HISTORY_TABLE_CONSTANTS.ROW_HEIGHT);
    
    // Ensure at least MIN_ROWS are displayed
    return Math.max(TRANSACTION_HISTORY_TABLE_CONSTANTS.MIN_ROWS, calculatedRows);
  };

  // Hook to monitor size changes
  useEffect(() => {
    const updateTransactionsPerPage = () => {
      const newTransactionsPerPage = calculateTransactionsPerPage();
      if (newTransactionsPerPage !== calculatedTransactionsPerPage && onTransactionsPerPageChange) {
        setCalculatedTransactionsPerPage(newTransactionsPerPage);
        onTransactionsPerPageChange(newTransactionsPerPage);
      }
    };

    // Add small delay to ensure DOM has rendered
    const timeoutId = setTimeout(updateTransactionsPerPage, TRANSACTION_HISTORY_TABLE_CONSTANTS.INITIAL_CALCULATION_DELAY);

    // Monitor window resize events
    const handleResize = () => {
      clearTimeout(timeoutId);
      setTimeout(updateTransactionsPerPage, TRANSACTION_HISTORY_TABLE_CONSTANTS.RESIZE_DEBOUNCE_DELAY);
    };

    window.addEventListener('resize', handleResize);
    
    // Monitor container size changes
    const resizeObserver = new ResizeObserver(handleResize);
    if (tableContainerRef.current) {
      resizeObserver.observe(tableContainerRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
      resizeObserver.disconnect();
    };
  }, [calculatedTransactionsPerPage, onTransactionsPerPageChange]);

  return (
    <Box ref={ref} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Search and Filter Section */}
      <Box sx={{ 
        p: 3, 
        borderBottom: `1px solid ${COLORS_ADMIN_PAGE.BORDER}`,
        display: 'flex',
        gap: 2,
        alignItems: 'center',
        height: TRANSACTION_HISTORY_TABLE_CONSTANTS.SEARCH_BAR_HEIGHT,
        boxSizing: 'border-box',
        flexShrink: 0
      }}>
        <TextField
          size="small"
          placeholder={TRANSACTION_HISTORY_TABLE_CONSTANTS.SEARCH_PLACEHOLDER}
          variant="outlined"
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          disabled={loading}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{
            width: 300,
            bgcolor: COLORS_CONFIG_PAGE.background,
            '& .MuiOutlinedInput-root': {
              '& fieldset': { border: 'none' },
            },
            borderRadius: 3,
          }}
        />
      </Box>

      {/* Table Section */}
      <Box 
        ref={tableContainerRef}
        sx={{ 
          flex: 1, 
          overflow: 'hidden', 
          display: 'flex', 
          flexDirection: 'column',
          minHeight: 0 // Important: allows flex item to shrink
        }}
      >
        <TableContainer component={Paper} sx={{ flex: 1, boxShadow: 'none', overflow: 'auto' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ height: TRANSACTION_HISTORY_TABLE_CONSTANTS.HEADER_HEIGHT }}>
                <TableCell><strong>Id</strong></TableCell>
                <TableCell><strong>User Id</strong></TableCell>
                <TableCell><strong>Timestamp</strong></TableCell>
                <TableCell><strong>Transaction Type</strong></TableCell>
                <TableCell><strong>Order Id</strong></TableCell>
                <TableCell><strong>Order Details</strong></TableCell>
                <TableCell><strong>Description</strong></TableCell>
                <TableCell align="right"><strong>Amount</strong></TableCell>
                <TableCell align="right"><strong>Previous Credits</strong></TableCell>
                <TableCell align="right"><strong>New Credits</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.map((transaction) => {
                const { label: typeLabel, color: typeColor } = getTransactionTypeInfo(transaction.transactionType);
                
                return (
                  <TableRow 
                    key={transaction.id || transaction.id} 
                    hover
                    sx={{ 
                      height: TRANSACTION_HISTORY_TABLE_CONSTANTS.ROW_HEIGHT,
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      }
                    }}
                  >
                    <TableCell>{transaction.id || transaction.id}</TableCell>
                    <TableCell>{transaction.userId}</TableCell>
                    <TableCell>{formatDateTime(transaction.timestamp)}</TableCell>
                    <TableCell>
                      <Chip
                        label={typeLabel}
                        size="small"
                        sx={{
                          bgcolor: typeColor,
                          color: 'white',
                          fontWeight: 'bold'
                        }}
                      />
                    </TableCell>
                    <TableCell>{transaction.paymentId || transaction.orderId || '-'}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ 
                        maxWidth: 150, 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {transaction.payoutId || transaction.orderDetails || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ 
                        maxWidth: 200, 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {transaction.description}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: transaction.amount >= 0 ? '#4caf50' : '#f44336',
                          fontWeight: 'bold'
                        }}
                      >
                        {transaction.amount >= 0 ? '+' : ''}{formatCurrency(transaction.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{formatCurrency(transaction.previousCredits)}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(transaction.newCredits)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
              {transactions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="textSecondary">
                      {loading ? TRANSACTION_HISTORY_TABLE_CONSTANTS.LOADING_MESSAGE : TRANSACTION_HISTORY_TABLE_CONSTANTS.NO_TRANSACTIONS_MESSAGE}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Pagination */}
      {totalTransactions > 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            borderTop: `1px solid ${COLORS_ADMIN_PAGE.BORDER}`,
            bgcolor: COLORS_ADMIN_PAGE.WHITE,
            height: TRANSACTION_HISTORY_TABLE_CONSTANTS.PAGINATION_HEIGHT,
            boxSizing: 'border-box',
            flexShrink: 0
          }}
        >
          <Typography variant="body2" color="textSecondary">
            {TRANSACTION_HISTORY_TABLE_CONSTANTS.SHOWING_TEXT} {((currentPage - 1) * transactionsPerPage) + 1} {TRANSACTION_HISTORY_TABLE_CONSTANTS.TO_TEXT}{' '}
            {Math.min(currentPage * transactionsPerPage, totalTransactions)} {TRANSACTION_HISTORY_TABLE_CONSTANTS.OF_TEXT}{' '}
            {totalTransactions} {TRANSACTION_HISTORY_TABLE_CONSTANTS.TRANSACTIONS_TEXT}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1 || loading}
            >
              <KeyboardArrowLeft />
            </Button>
            
            <Typography variant="body2" sx={{ mx: 2 }}>
              {currentPage} {TRANSACTION_HISTORY_TABLE_CONSTANTS.OF_TEXT} {Math.ceil(totalTransactions / transactionsPerPage)}
            </Typography>
            
            <Button
              variant="outlined"
              size="small"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === Math.ceil(totalTransactions / transactionsPerPage) || loading}
            >
              <KeyboardArrowRight />
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
});

TransactionHistoryTable.displayName = 'TransactionHistoryTable';

export default TransactionHistoryTable;